/**
 * BootstrapBrowser — the component library, Bootstrap-Docs style.
 *
 * Reads `registry.json` (via `/api/widget-native/registry`) and renders one
 * card per entry, grouped by a2ui provenance TIER (1.0-RC → 1.0-RC + extension →
 * Custom → Patterns → Tokens, see `a2uiTier.js`). The old L0–L4 kit layers are
 * no longer the grouping — a2ui has no layer notion — but each card still wears
 * its layer tag (the release lane: baked vs hot-updatable).
 *
 * The entries are TWO kinds, because the registry stores them differently:
 * `components[]` holds L1–L4, while L0 Foundations lives in the `tokens`
 * object as GROUPS of CSS variable names. Both are folded into one universe up
 * front so filtering, searching and every count treat them alike — otherwise
 * L0 shows "0 cards", which reads as "the design system has no foundations".
 *
 * The filter rows COMPOSE (tier ∧ family ∧ group ∧ search) and every count is
 * computed against the OTHER filters, never the whole registry: pick a tier
 * and the family counts collapse to what that tier actually contains. A
 * family with nothing left is disabled rather than hidden, so the row doesn't
 * jump around under the cursor.
 *
 * It also shows the DRIFT between the registry and this runtime's kit: a
 * component marked `render:"implemented"` with no web implementation is a bug
 * you want to see here, not on a blank widget page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ComponentCard from './ComponentCard';
import TokenGroupCard from './TokenGroupCard';
import { bootstrapHref, bootstrapAnchorId } from './studioNav';
import { missingImplementations, extraImplementations } from '../viewir/registry';
import { DERIVE_NAMES } from '../viewir/derives';
import { A2UI_TIERS, a2uiTier } from './a2uiTier';

const C = { text: '#1c1c1e', dim: '#6e6e73', border: '#e2e2ea', card: '#fff', accent: '#6E59F2' };

const chip = (on, disabled) => ({
  padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  border: `1px solid ${on ? C.accent : C.border}`,
  background: on ? C.accent : '#fff',
  color: on ? '#fff' : (disabled ? '#b8b8c0' : C.text),
  opacity: disabled ? 0.55 : 1,
  whiteSpace: 'nowrap',
});

const rowLabel = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase',
  color: C.dim, marginRight: 2,
};

/** Everything a full-text search should match, flattened once per entry. */
function haystack(e) {
  if (e.kind === 'tokens') return [e.id, e.tier, e.layer, e.family, ...e.names].join(' ').toLowerCase();
  return [
    // `group` is an optional authoring tag (registry `group`), so a batch of
    // components made together stays findable by one word after they scatter
    // across tiers and families.
    e.id, e.name, e.description, e.family, e.layer, e.tier, e.group || '',
    ...(e.variants || []), ...(e.states || []), ...(e.sealed || []), ...(e.overrides || []),
    ...(e.signals || []).map((s) => s.name), ...(e.slots || []).map((s) => s.name),
  ].join(' ').toLowerCase();
}

/**
 * `registry.tokens` → L0 entries, one per GROUP. `note` is prose, not a group.
 * Shaped like a component entry (id / layer / family) so a single filter
 * pipeline serves both kinds.
 */
function tokenEntries(registry) {
  const tokens = registry?.tokens || {};
  return Object.entries(tokens)
    .filter(([key, value]) => key !== 'note' && Array.isArray(value))
    .map(([group, names]) => ({ kind: 'tokens', id: group, layer: 'L0', family: 'tokens', tier: 'tokens', names }));
}

export default function BootstrapBrowser({ registry, locate = null }) {
  const navigate = useNavigate();
  // `locate` is a deep link (`?component=<id>`) from elsewhere — a studio module's
  // type badge, or a chat card's component name. It does NOT filter the library:
  // it SCROLLS the whole page to that component's card and highlights it, exactly
  // like following a GitHub README `#section` anchor. Search stays independent.
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('all');
  const [family, setFamily] = useState('all');
  const [group, setGroup] = useState('all');

  // Land on the located card: drop any active filters so it is guaranteed on the
  // page, then scroll it into view. `registry` in the deps means we re-run once it
  // has loaded and the cards are in the DOM. We RE-scroll a few times over ~1s
  // because the live component previews load async and keep shifting the layout
  // after the first paint — a single scroll lands, then the target moves under it.
  useEffect(() => {
    if (!locate) return;
    setTier('all');
    setFamily('all');
    setGroup('all');
    const id = bootstrapAnchorId(locate);
    let stopped = false;
    const timers = [];
    const scrollToIt = (smooth) => {
      const el = document.getElementById(id);
      // Optional-call, same as interpreter.jsx's scroll sink: jsdom (vitest) has no
      // scrollIntoView, and a missing scroll is a no-op, not a crash.
      el?.scrollIntoView?.({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    };
    // t=0 jump instantly, then smooth re-corrections as previews settle.
    scrollToIt(false);
    [120, 300, 600, 1000].forEach((ms) => {
      timers.push(setTimeout(() => { if (!stopped) scrollToIt(true); }, ms));
    });
    return () => { stopped = true; timers.forEach(clearTimeout); };
  }, [locate, registry]);

  // A card's "lays out" link is the same deep link, so it travels through the
  // URL rather than local state — the address bar keeps pointing at what you
  // are looking at, and back returns to the card you jumped from.
  const jumpTo = useCallback((type) => navigate(bootstrapHref(type)), [navigate]);

  // ONE universe: L0 token groups + L1–L3 components + the L4 patterns.
  //
  // THREE kinds now, because the registry stores three. `components[]` is
  // exactly what a blueprint may NAME, so the L4 recipes moved out of it into
  // `patterns[]` — a pattern is prose about how to compose, not a thing to
  // name. They still belong on this page (the recommended starting point for a
  // new widget is a recipe, not a component), so they are folded in here and
  // draw the same recipe card they always did.
  const universe = useMemo(
    () => [
      ...tokenEntries(registry),
      ...(registry?.components || []).map((c) => ({ ...c, kind: 'component', tier: a2uiTier(c) })),
      ...(registry?.patterns || []).map((p) => ({
        ...p, kind: 'component', isPattern: true, tier: 'pattern',
        layer: p.layer || 'L4', family: p.family || 'pattern',
      })),
    ],
    [registry],
  );

  const families = useMemo(() => [...new Set(universe.map((e) => e.family))].sort(), [universe]);
  // The authoring GROUP is orthogonal to family: family predicts a component's
  // ROLE (the nine-family roster is pinned), a group just names a batch that was
  // made together so it stays findable after it scatters across tiers.
  const groups = useMemo(
    () => [...new Set(universe.map((e) => e.group).filter(Boolean))].sort(), [universe],
  );

  // Search first, so typing narrows the chips as well as the cards.
  const searched = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return universe;
    return universe.filter((e) => haystack(e).includes(needle));
  }, [universe, q]);

  const filtered = useMemo(
    () => searched.filter(
      (e) => (tier === 'all' || e.tier === tier)
        && (family === 'all' || e.family === family)
        && (group === 'all' || e.group === group),
    ),
    [searched, tier, family, group],
  );

  // CROSS-FILTERED counts: a TIER chip counts within the active family, a
  // FAMILY chip within the active tier. The two rows therefore always agree
  // with each other and with the header.
  const tierCount = (id) => searched.filter(
    (e) => (id === 'all' || e.tier === id) && (family === 'all' || e.family === family)
      && (group === 'all' || e.group === group),
  ).length;
  const familyCount = (f) => searched.filter(
    (e) => (f === 'all' || e.family === f) && (tier === 'all' || e.tier === tier)
      && (group === 'all' || e.group === group),
  ).length;
  const groupCount = (g) => searched.filter(
    (e) => (g === 'all' || e.group === g) && (tier === 'all' || e.tier === tier)
      && (family === 'all' || e.family === family),
  ).length;

  // Sections in TIER order (what an a2ui renderer sees first), never alphabetical.
  const byTier = useMemo(() => {
    const m = new Map();
    for (const t of A2UI_TIERS) {
      const list = filtered.filter((e) => e.tier === t.id);
      if (list.length) m.set(t.id, list);
    }
    return m;
  }, [filtered]);

  const missing = missingImplementations(registry);
  const extra = extraImplementations(registry);
  const implemented = universe.filter((e) => e.kind === 'component' && e.render === 'implemented').length;
  const clearAll = () => { setQ(''); setTier('all'); setFamily('all'); setGroup('all'); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search components, ports, sealed rules, token names…"
          style={{
            flex: '1 1 260px', minWidth: 200, padding: '8px 12px', fontSize: 13,
            border: `1px solid ${C.border}`, borderRadius: 8, outline: 'none', background: '#fff',
          }}
        />
        <span style={{ fontSize: 12, color: C.dim }}>
          {filtered.length} / {universe.length} entries · {implemented} implemented components · {DERIVE_NAMES.length} named derives
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={rowLabel}>A2UI</span>
        <button type="button" onClick={() => setTier('all')} style={chip(tier === 'all')}>
          All · {tierCount('all')}
        </button>
        {A2UI_TIERS.map((t) => {
          const n = tierCount(t.id);
          const off = n === 0 && tier !== t.id;
          return (
            <button
              key={t.id} type="button" title={t.blurb} disabled={off}
              onClick={() => setTier(t.id)} style={chip(tier === t.id, off)}
            >
              {t.name} · {n}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={rowLabel}>Family</span>
        <button type="button" onClick={() => setFamily('all')} style={chip(family === 'all')}>
          All · {familyCount('all')}
        </button>
        {families.map((f) => {
          const n = familyCount(f);
          const off = n === 0 && family !== f;
          return (
            <button
              key={f} type="button" disabled={off}
              onClick={() => setFamily(f)} style={chip(family === f, off)}
            >
              {f} · {n}
            </button>
          );
        })}
      </div>

      {groups.length ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={rowLabel}>Group</span>
          <button type="button" onClick={() => setGroup('all')} style={chip(group === 'all')}>
            All · {groupCount('all')}
          </button>
          {groups.map((g) => {
            const n = groupCount(g);
            const off = n === 0 && group !== g;
            return (
              <button
                key={g} type="button" disabled={off}
                onClick={() => setGroup(g)} style={chip(group === g, off)}
              >
                {g} · {n}
              </button>
            );
          })}
        </div>
      ) : null}

      {(missing.length || extra.length) ? (
        <div style={{
          fontSize: 12, borderRadius: 10, padding: 12, lineHeight: 1.5,
          background: missing.length ? '#fff1f0' : '#f6f6f9',
          border: `1px solid ${missing.length ? '#ffd0cc' : C.border}`,
          color: missing.length ? '#b3261e' : C.dim,
        }}>
          {missing.length ? <div><strong>Registry ahead of the web kit:</strong> {missing.join(', ')} marked implemented with no web component.</div> : null}
          {extra.length ? <div>Web kit ahead of the registry: {extra.join(', ')}.</div> : null}
        </div>
      ) : null}

      {[...byTier.entries()].map(([tierId, list]) => {
        const meta = A2UI_TIERS.find((t) => t.id === tierId);
        return (
          <section key={tierId} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                A2UI · {meta?.name || tierId}{' '}
                <span style={{ fontWeight: 400, color: C.dim, fontSize: 12 }}>
                  {list.length}
                </span>
              </h3>
              {meta?.blurb ? (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: C.dim, maxWidth: 900, lineHeight: 1.5 }}>{meta.blurb}</p>
              ) : null}
            </div>
            <div style={{
              display: 'grid', gap: 16,
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            }}>
              {list.map((e) => (e.kind === 'tokens'
                ? <TokenGroupCard key={`t-${e.id}`} group={e.id} names={e.names} />
                : <ComponentCard key={e.id} comp={e} onJump={jumpTo} highlight={e.id === locate} />))}
            </div>
          </section>
        );
      })}

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: C.dim, fontSize: 13, lineHeight: 1.6 }}>
          <div>
            Nothing matches{q ? ` “${q}”` : ''}
            {tier !== 'all' ? ` in ${(A2UI_TIERS.find((t) => t.id === tier) || {}).name || tier}` : ''}
            {family !== 'all' ? ` · family “${family}”` : ''}.
          </div>
          <button type="button" onClick={clearAll} style={{ ...chip(false), marginTop: 12, padding: '6px 14px' }}>
            Clear filters
          </button>
        </div>
      ) : null}

      <section style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Named derives (closed set)</h3>
        <p style={{ margin: 0, fontSize: 12.5, color: C.dim, lineHeight: 1.5, maxWidth: 860 }}>
          A blueprint’s <code>{'{ "op": "derive", "expr": … }'}</code> may only name one of these. Both runtimes
          implement the same eight identically — adding one is an app-release change, not a blueprint change.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DERIVE_NAMES.map((d) => (
            <code key={d} style={{
              fontSize: 11.5, padding: '3px 9px', borderRadius: 6,
              background: '#f4f4f7', border: `1px solid ${C.border}`,
            }}>{d}</code>
          ))}
        </div>
      </section>
    </div>
  );
}
